import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { SURVEY_QUESTIONS } from "../../data/content";
import emailjs, {
  EMAILJS_SERVICE_ID,
  EMAILJS_TEMPLATE_ID,
  EMAILJS_PUBLIC_KEY,
} from "../../lib/emailjs";

const initial = {
  propertyType: "",
  propertySize: "",
  currentState: "",
  timeline: "",
  location: "",
  name: "",
  phone: "",
  email: "",
};

const RadioChip = ({ selected, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full border px-4 py-3 text-left text-sm font-medium transition-all ${
      selected
        ? "border-brand-accent bg-brand-accent/5 text-brand-primary shadow-[inset_0_0_0_1px_#D8232A]"
        : "border-brand-primary/15 bg-white text-brand-primary/70 hover:border-brand-primary/40 hover:text-brand-primary"
    }`}
  >
    {children}
  </button>
);

export default function SurveyForm({ idPrefix = "", className = "" }) {
  const prefix = idPrefix ? `${idPrefix}-` : "";
  const anchorPrefix = idPrefix ? `${idPrefix}-` : "";

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };
  const updateText = (key) => (ev) => set(key, ev.target.value);

  const validate = () => {
    const e = {};
    SURVEY_QUESTIONS.forEach((q) => {
      if (!form[q.id]) e[q.id] = "Kérjük, válasszon egy opciót.";
    });
    if (!form.location.trim()) e.location = "Kérjük, adja meg települését vagy irányítószámát.";
    if (!form.name.trim()) e.name = "Kérjük, adja meg a nevét.";
    if (!form.phone.trim()) e.phone = "Kérjük, adja meg telefonszámát.";
    if (!form.email.trim()) e.email = "Kérjük, adja meg az e-mail-címét.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Érvénytelen e-mail-cím.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      const firstErrKey = SURVEY_QUESTIONS.find((q) => !form[q.id])?.id ||
        (!form.location.trim() ? "location" :
         !form.name.trim() ? "name" :
         !form.phone.trim() ? "phone" :
         !form.email.trim() ? "email" : null);
      if (firstErrKey) {
        const el = document.querySelector(`[data-error-anchor="${anchorPrefix}${firstErrKey}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    setSubmitting(true);
    try {
      const templateParams = {
        propertyType: form.propertyType,
        propertySize: form.propertySize,
        currentState: form.currentState,
        timeline: form.timeline,
        location: form.location.trim(),
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      };
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      if (response.status !== 200) throw new Error(`EmailJS status ${response.status}`);

      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
      setForm(initial);
      toast.success("Köszönjük! Hamarosan felvesszük Önnel a kapcsolatot.", {
        description: "Válaszait elküldtük, díjmentes felmérés egyeztetése hamarosan.",
      });
    } catch (err) {
      console.error("EmailJS send failed:", err, "status:", err?.status, "text:", err?.text);
      toast.error("Az üzenet elküldése nem sikerült.", {
        description:
          err?.text ||
          "Kérjük, próbálja újra később, vagy hívjon minket telefonon.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full border bg-white px-4 py-3.5 text-brand-primary placeholder:text-brand-primary/40 outline-none transition-colors focus:border-brand-primary";

  return (
    <form
      data-testid={`${prefix}contact-form`}
      onSubmit={onSubmit}
      noValidate
      className={`bg-white p-6 shadow-[0_8px_30px_rgba(10,37,64,0.05)] sm:p-8 md:p-10 ${className}`}
    >
      <div className="space-y-8">
        {SURVEY_QUESTIONS.map((q) => (
          <div key={q.id} data-error-anchor={`${anchorPrefix}${q.id}`}>
            <label className="mb-3 flex items-baseline gap-3">
              <span className="font-mono text-sm font-semibold text-brand-accent">
                {q.number}
              </span>
              <span className="font-heading text-base font-bold text-brand-primary md:text-lg">
                {q.label}
              </span>
            </label>
            <div className={`grid grid-cols-1 gap-2 ${q.cols}`}>
              {q.options.map((opt) => (
                <RadioChip
                  key={opt}
                  selected={form[q.id] === opt}
                  onClick={() => set(q.id, opt)}
                  testid={`${prefix}option-${q.id}-${opt.substring(0, 12).replace(/\s+/g, "-")}`}
                >
                  {opt}
                </RadioChip>
              ))}
            </div>
            {errors[q.id] && (
              <p data-testid={`${prefix}error-${q.id}`} className="mt-2 text-xs text-red-600">
                {errors[q.id]}
              </p>
            )}
          </div>
        ))}

        <div data-error-anchor={`${anchorPrefix}location`}>
          <label className="mb-3 flex items-baseline gap-3">
            <span className="font-mono text-sm font-semibold text-brand-accent">05</span>
            <span className="font-heading text-base font-bold text-brand-primary md:text-lg">
              Település / irányítószám
            </span>
          </label>
          <input
            data-testid={`${prefix}input-location`}
            type="text"
            value={form.location}
            onChange={updateText("location")}
            placeholder="pl. 1114 Budapest vagy Kecskemét"
            className={`${inputBase} ${errors.location ? "border-red-500" : "border-brand-primary/15"}`}
          />
          {errors.location && <p data-testid={`${prefix}error-location`} className="mt-1.5 text-xs text-red-600">{errors.location}</p>}
        </div>

        <div data-error-anchor={`${anchorPrefix}name`}>
          <label className="mb-3 flex items-baseline gap-3">
            <span className="font-mono text-sm font-semibold text-brand-accent">06</span>
            <span className="font-heading text-base font-bold text-brand-primary md:text-lg">
              Elérhetőségei
            </span>
          </label>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <input
                data-testid={`${prefix}input-name`}
                type="text"
                value={form.name}
                onChange={updateText("name")}
                placeholder="Név"
                className={`${inputBase} ${errors.name ? "border-red-500" : "border-brand-primary/15"}`}
              />
              {errors.name && <p data-testid={`${prefix}error-name`} className="mt-1.5 text-xs text-red-600">{errors.name}</p>}
            </div>
            <div>
              <input
                data-testid={`${prefix}input-phone`}
                type="tel"
                value={form.phone}
                onChange={updateText("phone")}
                placeholder="Telefonszám"
                className={`${inputBase} ${errors.phone ? "border-red-500" : "border-brand-primary/15"}`}
              />
              {errors.phone && <p data-testid={`${prefix}error-phone`} className="mt-1.5 text-xs text-red-600">{errors.phone}</p>}
            </div>
          </div>
          <div className="mt-4">
            <input
              data-testid={`${prefix}input-email`}
              type="email"
              value={form.email}
              onChange={updateText("email")}
              placeholder="E-mail cím"
              className={`${inputBase} ${errors.email ? "border-red-500" : "border-brand-primary/15"}`}
            />
            {errors.email && <p data-testid={`${prefix}error-email`} className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
          </div>
        </div>
      </div>

      <button
        data-testid={`${prefix}contact-submit`}
        type="submit"
        disabled={submitting}
        className="group mt-8 flex w-full items-center justify-center gap-2 bg-brand-accent px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-brand-accentDark disabled:opacity-60 sm:w-auto"
      >
        {submitting ? "Küldés..." : "Ingyenes felmérést kérek"}
        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
      </button>

      <p className="mt-4 text-xs text-brand-primary/50">
        Az elküldéssel elfogadja, hogy adatait ajánlatkéréssel kapcsolatban felhasználjuk.
      </p>
    </form>
  );
}
